import { useState } from 'react';
import type { Canal, CodigoPedido, OperacionesClienteFinal, ResultadoIdentificacion, TokenCliente } from '../api/ops/clienteFinal';
import { ErrorApi } from '../api/problema';
import { Campo } from '../componentes/Campo';
import { Chip } from '../componentes/Chip';
import { Icono } from '../componentes/Icono';
import { emailValido, errorDeCodigo, esCodigo, etiquetaCanal, nombreCanal, nombreDePila, telefonoLegible, telefonoValido, textoDeError, type ErrorCodigo } from './logica';
import { AvisoPc, Baldosa, BotonPc, EnlaceLegal, LegalCaptcha } from './Pagina';
import { PasoCodigo } from './PasoCodigo';
import { D, ENLACES_LEGALES, T, VERSION_AVISO, VERSION_CONDICIONES } from './textos';

/**
 * 2.9 Identificarse con un código: el bloque compartido de la web pública, los widgets, los enlaces y «Mis datos» (G157).
 * Teléfono o email (T5) → código de 6 números (T7, T8) → si es una cuenta nueva, el nombre (T9, T10, «Aceptar y crear mi
 * cuenta», G190) → identificado. El email sin cuenta se dice solo tras el código correcto (T12, G154).
 *
 * En esta fase solo lo usa «Entra en Mis datos» (sin negocio: T3 y T4b, G272), «Tu sesión ha caducado» y «Confirma que
 * eres tú» (T31). Queda preparado para las páginas de un negocio (T1/T2, T4 entero, T11 y la casilla T13 en una reserva,
 * con `identifyCustomerInBusiness` y la clave pública del negocio), que llegarán con la web pública.
 */
export interface NegocioDeLaPagina {
    id: string;
    nombre: string;
    /** `privacy_url` (nunca nulo, G271). Vacío si la página no la sabe (el widget, que no la trae): texto sin enlace. */
    privacidad: string;
    /** Título del paso en una página que no es de reservas (Pedir cita, 9.9: «Tus datos», T2). */
    titulo?: { titulo: string; sub: string };
    /** Clave pública del negocio (`X-Api-Key` de `identifyCustomerInBusiness`). */
    clavePublica: string;
    origen?: 'web' | 'widget' | 'list_link';
    /** Dentro de una reserva (T2 y T13): canal de la confirmación y canal de la casilla comercial. */
    reserva?: { canalConfirmacion: Canal; canalComercial: Canal };
}

/** Con qué se identificó (G787: el pase de una compra va por ahí), como se enseña. */
export interface ContactoUsado {
    tipo: 'telefono' | 'email';
    valor: string;
}

export type ContextoIdentificacion =
    | { tipo: 'mis-datos' }
    | { tipo: 'mis-pagos' }
    | { tipo: 'mis-pases' }
    | { tipo: 'mis-mensajes' }
    | { tipo: 'mis-conversaciones' }
    | { tipo: 'mis-citas' }
    | { tipo: 'sesion-caducada' }
    | { tipo: 'negocio'; negocio: NegocioDeLaPagina }
    /** `telefono` nulo: una cuenta nacida solo con el email (G365) confirma con un código a su email. */
    | { tipo: 'reauth'; para: 'borrar' | 'telefono' | 'unir'; telefono: string | null; email: string | null };

export interface PropsIdentificarse {
    ops: OperacionesClienteFinal;
    contexto: ContextoIdentificacion;
    captcha: (accion: string) => Promise<string | null>;
    /** Con el token de cliente recién emitido (se guarda en el almacén de la zona). */
    onToken: (token: TokenCliente) => void;
    /** Páginas de un negocio: tras «Seguir», con el perfil del cliente en ese negocio. */
    onIdentificadoEnNegocio?: (resultado: ResultadoIdentificacion, contacto: ContactoUsado) => void;
    /** «No soy yo» (páginas de un negocio): revoca el token. */
    onNoSoyYo?: () => void;
    /**
     * Negocio de la página que usa la clave de plataforma (G897): «Mis documentos» con `?negocio=` lo pasa en
     * `requestCustomerCode` (`business_id`), y el teléfono sin prefijo se normaliza con su país.
     */
    negocioDelCodigo?: string | null;
    ahora?: () => number;
    /** `oscuro` dentro del widget oscuro (8.11, 9.9 y 10.13 en 13.8, G618): los RV Campo en oscuro. */
    tema?: 'claro' | 'oscuro';
}

type TipoContacto = 'telefono' | 'email';

type Paso =
    | { p: 'contacto' }
    | { p: 'codigo'; pedido: CodigoPedido; canal: Canal; desde: number }
    | { p: 'nombre'; pedido: CodigoPedido; canal: Canal; desde: number }
    | { p: 'sin-cuenta' }
    | { p: 'hecho'; token: TokenCliente };

export function Identificarse({ ops, contexto, captcha, onToken, onIdentificadoEnNegocio, onNoSoyYo, negocioDelCodigo = null, ahora = Date.now, tema = 'claro' }: PropsIdentificarse) {
    const reauth = contexto.tipo === 'reauth';
    const [paso, setPaso] = useState<Paso>({ p: 'contacto' });
    const [tipo, setTipo] = useState<TipoContacto>('telefono');
    const [valor, setValor] = useState(reauth ? (contexto.telefono ?? contexto.email ?? '') : '');
    const [errorCampo, setErrorCampo] = useState<string | null>(null);
    const [errorForm, setErrorForm] = useState<string | null>(null);
    const [captchaFallo, setCaptchaFallo] = useState(false);
    const [codigo, setCodigo] = useState('');
    const [errorCodigo, setErrorCodigo] = useState<ErrorCodigo | null>(null);
    const [otroEnviado, setOtroEnviado] = useState(false);
    const [nombre, setNombre] = useState('');
    const [email, setEmail] = useState('');
    const [errores, setErrores] = useState<{ nombre?: string; email?: string }>({});
    const [comercial, setComercial] = useState(false);
    const [ocupado, setOcupado] = useState(false);

    const negocio = contexto.tipo === 'negocio' ? contexto.negocio : null;
    const esTelefono = reauth ? contexto.telefono !== null : tipo === 'telefono';
    const mostrado = reauth ? (contexto.telefono === null ? (contexto.email ?? '') : telefonoLegible(contexto.telefono)) : valor.trim();

    const limpiarErrores = () => {
        setErrorCampo(null);
        setErrorForm(null);
        setCaptchaFallo(false);
    };

    /** requestCustomerCode con el canal elegido (WhatsApp, o SMS desde «¿No te llega?»), el captcha y la clave pública. */
    const pedir = async (canal: Canal, reenvio: boolean) => {
        if (!reauth && !reenvio) {
            if (esTelefono ? !telefonoValido(valor) : !emailValido(valor)) {
                setErrorCampo(esTelefono ? D.escribeTelefono : D.escribeEmail);
                return;
            }
        }
        setOcupado(true);
        limpiarErrores();
        try {
            const delNegocio = negocioDelCodigo === null ? {} : { business_id: negocioDelCodigo };
            const datos = esTelefono ? { phone: reauth ? (contexto.telefono ?? '') : valor.trim(), channel: canal, ...delNegocio } : { email: valor.trim(), channel: canal, ...delNegocio };
            const pedido = await ops.requestCustomerCode(datos, await captcha('codigo_cliente'));
            setCodigo('');
            setErrorCodigo(null);
            setOtroEnviado(reenvio);
            setPaso({ p: 'codigo', pedido, canal, desde: ahora() + (pedido.resend_after_seconds ?? 0) * 1000 });
        } catch (e) {
            if (esCodigo(e, 'captcha_required')) {
                setCaptchaFallo(true);
                if (reenvio) {
                    setErrorCodigo({ tipo: 'aviso', texto: D.captcha });
                }
            } else if (e instanceof ErrorApi && e.codigo === 'validation_failed' && !reenvio) {
                setErrorCampo(esTelefono ? D.escribeTelefono : D.escribeEmail);
            } else if (reenvio) {
                setErrorCodigo({ tipo: 'aviso', texto: textoDeError(e) });
            } else {
                setErrorForm(textoDeError(e));
            }
        } finally {
            setOcupado(false);
        }
    };

    /** confirmCustomerCode; con `cuentaNueva`, crea la Cuenta Reservaria (T10, G190). */
    const confirmar = async (verificacion: CodigoPedido, canal: Canal, desde: number, c: string, cuentaNueva: boolean) => {
        if (c.length !== 6) {
            setErrorCodigo({ tipo: 'incorrecto', quedan: 3 });
            return;
        }
        if (cuentaNueva) {
            const nuevos: typeof errores = {};
            if (nombre.trim() === '') nuevos.nombre = D.escribeNombre;
            if (email.trim() !== '' && !emailValido(email)) nuevos.email = D.escribeEmail;
            setErrores(nuevos);
            if (Object.keys(nuevos).length > 0) return;
        }
        setOcupado(true);
        setErrorForm(null);
        try {
            const token = await ops.confirmCustomerCode(
                verificacion.verification_id,
                c,
                cuentaNueva
                    ? { display_name: nombre.trim(), ...(email.trim() === '' || !esTelefono ? {} : { email: email.trim() }), notice_version: VERSION_AVISO, terms_version: VERSION_CONDICIONES }
                    : undefined,
            );
            onToken(token);
            if (negocio !== null) {
                setPaso({ p: 'hecho', token });
            }
        } catch (e) {
            const malo = errorDeCodigo(e);
            if (malo !== null) {
                setErrorCodigo(malo);
                if (malo.tipo === 'incorrecto') setCodigo('');
                setPaso({ p: 'codigo', pedido: verificacion, canal, desde });
            } else if (esCodigo(e, 'validation_failed') && (e as ErrorApi).errores.some((x) => x.field === 'new_account.display_name')) {
                // Cuenta nueva: sin gastar el intento, se pide el nombre (T9).
                setPaso({ p: 'nombre', pedido: verificacion, canal, desde });
            } else if (esCodigo(e, 'validation_failed') && cuentaNueva && (e as ErrorApi).errores.some((x) => x.field === 'new_account.email')) {
                setErrores({ email: D.escribeEmail });
            } else if (esCodigo(e, 'not_found') && !esTelefono) {
                setPaso({ p: 'sin-cuenta' });
            } else if (cuentaNueva) {
                setErrorForm(textoDeError(e));
            } else {
                setErrorCodigo({ tipo: 'aviso', texto: textoDeError(e) });
            }
        } finally {
            setOcupado(false);
        }
    };

    /** Páginas de un negocio: el perfil en ese negocio con el canal y, si la marcó, la casilla comercial (T13, G29). */
    const seguir = async () => {
        if (negocio === null) return;
        setOcupado(true);
        setErrorForm(null);
        try {
            const reserva = negocio.reserva;
            const r = await ops.identifyCustomerInBusiness(negocio.id, negocio.clavePublica, {
                source: negocio.origen ?? 'web',
                ...(reserva === undefined ? {} : { preferred_channel: reserva.canalConfirmacion }),
                ...(reserva !== undefined && comercial ? { consents: [{ channel: reserva.canalComercial, purpose: 'marketing' as const, granted: true, notice_version: VERSION_AVISO }] } : {}),
            });
            const telefono = paso.p === 'hecho' ? paso.token.account.phone : null;
            onIdentificadoEnNegocio?.(r, esTelefono ? { tipo: 'telefono', valor: telefono == null ? mostrado : telefonoLegible(telefono) } : { tipo: 'email', valor: mostrado });
        } catch (e) {
            setErrorForm(textoDeError(e));
        } finally {
            setOcupado(false);
        }
    };

    const cambiarTipo = (t: TipoContacto) => {
        if (t !== tipo) {
            setTipo(t);
            setValor('');
        }
        limpiarErrores();
    };

    const titulos = (): { titulo: string; sub: string } => {
        switch (contexto.tipo) {
            case 'mis-datos':
                return { titulo: T.t3.titulo, sub: T.t3.sub };
            case 'mis-pagos':
                return D.misPagos;
            case 'mis-pases':
                return D.misPases;
            case 'mis-mensajes':
                return D.misMensajes;
            case 'mis-conversaciones':
                return D.misConversaciones;
            case 'sesion-caducada':
                return D.sesionCaducada;
            case 'reauth':
                return {
                    titulo: T.t31.titulo,
                    sub: contexto.telefono === null ? T.t31.subEmail(contexto.para, contexto.email ?? '') : T.t31.sub(contexto.para, telefonoLegible(contexto.telefono)),
                };
            case 'mis-citas':
                return D.misCitas;
            case 'negocio':
                if (contexto.negocio.titulo !== undefined) return contexto.negocio.titulo;
                return contexto.negocio.reserva === undefined ? { titulo: T.t1.titulo, sub: T.t1.sub(contexto.negocio.nombre) } : { titulo: T.t2.titulo, sub: T.t2.sub };
        }
    };

    if (paso.p === 'codigo') {
        const destino = esTelefono ? (paso.canal === 'sms' ? T.t7.sms(mostrado) : T.t7.whatsapp(mostrado)) : T.t7.email(mostrado);
        return (
            <PasoCodigo
                destino={destino}
                cambiar={reauth ? undefined : { texto: esTelefono ? D.cambiarTelefono : D.cambiarEmail, accion: () => { setPaso({ p: 'contacto' }); limpiarErrores(); } }}
                expira={paso.pedido.expires_at}
                reenviarDesde={paso.desde}
                codigo={codigo}
                onCodigo={(c) => {
                    setCodigo(c);
                    if (errorCodigo?.tipo === 'incorrecto' || errorCodigo?.tipo === 'aviso') setErrorCodigo(null);
                    setOtroEnviado(false);
                }}
                error={errorCodigo}
                otroEnviado={otroEnviado}
                ocupado={ocupado}
                noLlega={
                    esTelefono && paso.canal !== 'sms'
                        ? {
                              sms: () => void pedir('sms', true),
                              email: reauth ? undefined : () => { cambiarTipo('email'); setPaso({ p: 'contacto' }); },
                          }
                        : undefined
                }
                onComprobar={(c) => void confirmar(paso.pedido, paso.canal, paso.desde, c, false)}
                onReenviar={() => void pedir(paso.canal, true)}
                ahora={ahora}
            />
        );
    }

    if (paso.p === 'nombre') {
        return (
            <section className="rv-pc-card" aria-labelledby="rv-pc-nombre-t">
                <div className="rv-pc-card__cab">
                    <h2 id="rv-pc-nombre-t">{T.t9.titulo}</h2>
                    <span>{T.t9.sub(mostrado)}</span>
                </div>
                <div className="rv-pc-campos">
                    <Campo etiqueta={T.t9.nombre} valor={nombre} onChange={(v) => { setNombre(v); setErrores({ ...errores, nombre: undefined }); }} autocompletar="name" mayusculas="words" max={120} error={errores.nombre} tema={tema} />
                    {/* Con el código por email (G365, hasta Canales) la cuenta nace con ese email: no se pide otro. */}
                    {esTelefono && <Campo etiqueta={T.t9.email} tipo="email" valor={email} onChange={(v) => { setEmail(v); setErrores({ ...errores, email: undefined }); }} error={errores.email} tema={tema} />}
                    {/* T9: la ayuda del email en su propia línea (la de RV Campo es de una sola línea). */}
                    {esTelefono && errores.email === undefined && <span className="rv-pc-ayuda">{T.t9.ayudaEmail}</span>}
                </div>
                <div className="rv-pc-capa">
                    <Icono nombre="shield-check" />
                    <span>
                        <span>{T.t10}</span>
                        <span className="rv-pc-capa__enlaces">
                            <EnlaceLegal url={ENLACES_LEGALES.condiciones}>{T.condicionesCuenta}</EnlaceLegal>
                            <EnlaceLegal url={ENLACES_LEGALES.privacidad}>{T.privacidadReservaria}</EnlaceLegal>
                        </span>
                    </span>
                </div>
                {errorForm !== null && <AvisoPc texto={errorForm} />}
                <div className="rv-pc-acciones">
                    <BotonPc texto={T.aceptarYCrear} icono="arrow-right" ocupado={ocupado} ocupadoTexto={D.creandoCuenta} onClick={() => void confirmar(paso.pedido, paso.canal, paso.desde, codigo, true)} />
                </div>
            </section>
        );
    }

    if (paso.p === 'sin-cuenta') {
        return (
            <section className="rv-pc-card" aria-labelledby="rv-pc-sin-t">
                <Baldosa icono="mail" fondo="var(--rv-paper)" color="var(--rv-ink)" />
                <div className="rv-pc-card__cab">
                    <h2 id="rv-pc-sin-t">{T.t12.titulo}</h2>
                    <span>{T.t12.sub}</span>
                </div>
                <BotonPc texto={D.usarTelefono} icono="smartphone" onClick={() => { setTipo('telefono'); setValor(''); setPaso({ p: 'contacto' }); }} />
            </section>
        );
    }

    if (paso.p === 'hecho') {
        const cuenta = paso.token.account;
        const reserva = negocio?.reserva;
        return (
            <section className="rv-pc-card" aria-labelledby="rv-pc-hecho-t">
                <Baldosa icono="check" fondo="var(--rv-red)" color="#fff" />
                <div className="rv-pc-card__cab">
                    <h2 id="rv-pc-hecho-t">{paso.token.account_created ? D.bienvenida(nombreDePila(cuenta.display_name)) : D.hola(nombreDePila(cuenta.display_name))}</h2>
                    <span>{paso.token.account_created ? T.t11.creada : T.t11.existia}</span>
                    <span style={{ alignSelf: 'flex-start' }}>
                        <Chip estado="confirmed">{T.t11.chip}</Chip>
                    </span>
                </div>
                {reserva !== undefined && negocio !== null && (
                    <div className="rv-pc-campos">
                        <div className="rv-pc-card__cab">
                            <strong>{T.t13.titulo(etiquetaCanal(reserva.canalConfirmacion))}</strong>
                            <span>{T.t13.sub}</span>
                        </div>
                        {/* T13: la casilla comercial, nunca marcada de antemano (G29). */}
                        <label className="rv-pc-casilla">
                            <input type="checkbox" checked={comercial} onChange={(e) => { setComercial(e.target.checked); }} />
                            <span>{T.t13.casilla(negocio.nombre, nombreCanal(reserva.canalComercial))}</span>
                        </label>
                        <span className="rv-pc-ayuda" style={{ marginTop: 0, padding: '0 0 0 34px' }}>
                            {T.t13.ayudaAntes}
                            <EnlaceLegal url={negocio.privacidad === '' ? null : negocio.privacidad}>{T.privacidadDe(negocio.nombre)}</EnlaceLegal>.
                        </span>
                    </div>
                )}
                {errorForm !== null && <AvisoPc texto={errorForm} />}
                <div className="rv-pc-acciones">
                    <BotonPc texto={D.seguir} icono="arrow-right" ocupado={ocupado} ocupadoTexto={D.cargando} onClick={() => void seguir()} />
                    {onNoSoyYo !== undefined && (
                        <button type="button" className="rv-pc-l rv-pc-l--centro" onClick={onNoSoyYo}>
                            <Icono nombre="log-out" />
                            <span>{T.t11.noSoyYo}</span>
                        </button>
                    )}
                </div>
            </section>
        );
    }

    // Teléfono o email (o, para «Confirma que eres tú», solo el botón: el código va al teléfono de la cuenta).
    const { titulo, sub } = titulos();
    const conCapa = contexto.tipo === 'mis-datos' || contexto.tipo === 'mis-pagos' || contexto.tipo === 'mis-pases' || contexto.tipo === 'mis-mensajes' || contexto.tipo === 'mis-conversaciones' || contexto.tipo === 'mis-citas' || contexto.tipo === 'negocio';
    return (
        <section className="rv-pc-card" aria-labelledby="rv-pc-id-t">
            <div className="rv-pc-card__cab">
                <h2 id="rv-pc-id-t">{titulo}</h2>
                <span>{sub}</span>
            </div>
            {!reauth && (
                <div className="rv-pc-campos">
                    <div role="radiogroup" aria-label="Cómo quieres recibir el código" className="rv-pc-tipos">
                        {(
                            [
                                ['telefono', D.telefono, 'smartphone'],
                                ['email', D.email, 'mail'],
                            ] as const
                        ).map(([t, texto, icono]) => (
                            <button key={t} type="button" role="radio" aria-checked={tipo === t} className="rv-pc-tipo" onClick={() => { cambiarTipo(t); }}>
                                <Icono nombre={icono} />
                                <span>{texto}</span>
                            </button>
                        ))}
                    </div>
                    <Campo
                        etiqueta={esTelefono ? D.telefono : D.email}
                        tipo={esTelefono ? 'tel' : 'email'}
                        valor={valor}
                        onChange={(v) => { setValor(v); limpiarErrores(); }}
                        max={esTelefono ? 20 : 254}
                        error={errorCampo}
                        ayuda={esTelefono ? T.t5.telefono : T.t5.email}
                        onEnter={() => void pedir(esTelefono ? 'whatsapp' : 'email', false)}
                        tema={tema}
                    />
                </div>
            )}
            {errorForm !== null && <AvisoPc texto={errorForm} />}
            <div className="rv-pc-acciones">
                <BotonPc texto={D.enviarmeCodigo} icono="send" ocupado={ocupado} ocupadoTexto={D.enviando} onClick={() => void pedir(esTelefono ? 'whatsapp' : 'email', false)} />
                {/* G83: sin borrar lo escrito y con el botón activo. */}
                {captchaFallo && <AvisoPc texto={D.captcha} />}
            </div>
            {conCapa && (
                <div className="rv-pc-info">
                    <Icono nombre="info" />
                    <span>
                        {negocio === null ? T.t4b : T.t4(negocio.nombre)}
                        <span className="rv-pc-info__enlaces">
                            <EnlaceLegal url={ENLACES_LEGALES.privacidad}>{T.privacidadReservaria}</EnlaceLegal>
                            {negocio !== null && <EnlaceLegal url={negocio.privacidad === '' ? null : negocio.privacidad}>{T.privacidadDe(negocio.nombre)}</EnlaceLegal>}
                        </span>
                    </span>
                </div>
            )}
            <LegalCaptcha />
        </section>
    );
}
